import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import { Logo } from './Logo';
import { cn } from '../lib/utils';

const navLinks = [
  { name: 'Forside', href: '/' },
  { name: 'Ejendomsservice', href: '/ejendomsservice' },
  {
    name: 'Andre opgaver',
    href: '/andre-opgaver',
    submenu: [
      { name: 'Græsslåning', href: '/andre-opgaver/graesslaaning' },
      { name: 'Flishugning', href: '/andre-opgaver/flishugning' },
      { name: 'Havearbejde', href: '/andre-opgaver/havearbejde' },
    ],
  },
  { name: 'Galleri', href: '/galleri' },
  { name: 'Kontakt', href: '/kontakt' },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
        scrolled ? 'bg-emerald-50/95 backdrop-blur-lg py-3 border-emerald-200 shadow-sm' : 'bg-white/70 backdrop-blur-lg py-5 border-transparent'
      )}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        <Link to="/" className="flex items-center group">
          <Logo className="h-8 md:h-10 transition-transform group-hover:scale-105" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <div key={link.name} className="relative group">
              {link.submenu ? (
                <div className="flex items-center gap-1 cursor-pointer py-2">
                  <Link
                    to={link.href}
                    className={cn(
                      'text-sm font-medium transition-colors hover:text-emerald-600',
                      location.pathname.startsWith(link.href) ? 'text-emerald-700' : 'text-emerald-900'
                    )}
                  >
                    {link.name}
                  </Link>
                  <ChevronDown className="w-4 h-4 text-emerald-600" />
                  
                  {/* Dropdown */}
                  <div className="absolute top-full left-0 mt-2 w-48 bg-emerald-50 border border-emerald-200 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 overflow-hidden">
                    {link.submenu.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.href}
                        className="block px-4 py-3 text-sm text-emerald-900 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  to={link.href}
                  className={cn(
                    'text-sm font-medium transition-colors hover:text-emerald-600 py-2',
                    location.pathname === link.href ? 'text-emerald-700' : 'text-emerald-900'
                  )}
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}
          
          <motion.a
            href="tel:+4542442359"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-red-700 transition-all shadow-md hover:shadow-lg"
          >
            <Phone className="w-4 h-4" />
            42 44 23 59
          </motion.a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 text-emerald-900"
          onClick={() => setIsOpen(!isOpen)}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <X className="w-6 h-6" />
              </motion.div>
            ) : (
              <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <Menu className="w-6 h-6" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden absolute top-full left-0 right-0 bg-emerald-50 border-b border-emerald-200 overflow-hidden"
          >
            <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.submenu ? (
                    <div className="flex flex-col gap-2">
                      <button 
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="flex items-center justify-between text-lg font-medium text-emerald-900 py-2"
                      >
                        {link.name}
                        <ChevronDown className={cn("w-5 h-5 transition-transform", dropdownOpen && "rotate-180")} />
                      </button>
                      <AnimatePresence>
                        {dropdownOpen && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="flex flex-col gap-2 pl-4 border-l-2 border-emerald-100 overflow-hidden"
                          >
                            {link.submenu.map((sub) => (
                              <Link
                                key={sub.name}
                                to={sub.href}
                                className="text-emerald-700 py-2"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-lg font-medium text-emerald-900 py-2 block"
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <motion.a
                href="tel:+4542442359"
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-2 bg-red-600 text-white px-5 py-4 rounded-xl text-lg font-semibold mt-4"
              >
                <Phone className="w-5 h-5" />
                Ring op: 42 44 23 59
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
